import React, { useState, useEffect } from "react";
import "./certificate_section.css";

export function CertificateSection() {
    const [certificates, setCertificates] = useState([]);
    const [activeFilter, setActiveFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Define API URL from environment variable
    const API_URL = `${process.env.REACT_APP_API_URL}/certificates`;

    // Parse date string to Date object for sorting
    const parseDate = (dateString) => {
        if (!dateString) return new Date(0); // Return epoch for invalid dates

        // Handle various date formats: "Oct 2025", "October 2025", "Dec 2024", etc.
        const months = {
            jan: 0, january: 0,
            feb: 1, february: 1,
            mar: 2, march: 2,
            apr: 3, april: 3,
            may: 4,
            jun: 5, june: 5,
            jul: 6, july: 6,
            aug: 7, august: 7,
            sep: 8, september: 8,
            oct: 9, october: 9,
            nov: 10, november: 10,
            dec: 11, december: 11
        };

        const parts = dateString.trim().toLowerCase().split(/\s+/);

        if (parts.length >= 2) {
            const monthStr = parts[0];
            const year = parseInt(parts[1]);
            const month = months[monthStr];

            if (month !== undefined && !isNaN(year)) {
                return new Date(year, month);
            }
        }

        // Fallback: try parsing as is
        return new Date(dateString);
    };

    // Sort certificates by date (most recent first)
    const sortCertificatesByDate = (certs) => {
        return [...certs].sort((a, b) => {
            const dateA = parseDate(a.date);
            const dateB = parseDate(b.date);
            return dateB - dateA; // Descending order (newest first)
        });
    };

    // Fetch certificates data from API
    useEffect(() => {
        const fetchCertificates = async () => {
            try {
                setLoading(true);

                const response = await fetch(API_URL, {
                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json",
                    },
                });

                if (!response.ok) {
                    throw new Error(`API returned error: ${response.status} ${response.statusText}`);
                }

                const contentType = response.headers.get("content-type");
                if (!contentType || !contentType.includes("application/json")) {
                    throw new Error(`Expected JSON but got ${contentType || "unknown content type"}`);
                }

                const data = await response.json();

                if (!data || !data.certificates || !Array.isArray(data.certificates)) {
                    throw new Error("Invalid data structure received from API");
                }

                // Sort certificates by date before setting state
                const sortedCertificates = sortCertificatesByDate(data.certificates);
                setCertificates(sortedCertificates);
                setError(null);
            } catch (err) {
                console.error("Failed to fetch certificates:", err);
                setError(`API error: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchCertificates();
    }, [API_URL]);

    // Extract unique categories dynamically
    const categories = [
        { value: "all", label: "All" },
        ...Array.from(new Set(certificates.map(cert => cert.category))).map(category => ({
            value: category,
            label: category.charAt(0).toUpperCase() + category.slice(1),
        }))
    ];

    // Filter certificates based on active filter (already sorted)
    const filteredCertificates = activeFilter === "all"
        ? certificates
        : certificates.filter(cert => cert.category === activeFilter);

    // Handle image error
    const handleImageError = (e) => {
        e.target.src = "/api/placeholder/400/300"; // Fallback image
    };

    // Handle certificate view
    const viewCertificate = (imageUrl, title) => {
        if (imageUrl) {
            window.open(imageUrl, "_blank");
        } else {
            alert(`Certificate preview not available for: ${title}`);
        }
    };

    return (
        <div className="certificate-section">
            <div className="certificate-header">
                <h1>Certificates & Achievements</h1>
                <p>Browse through my professional certifications and achievements</p>
            </div>

            <div className="certificate-filters">
                {categories.map(category => (
                    <button
                        key={category.value}
                        className={`filter-btn ${activeFilter === category.value ? "active" : ""}`}
                        onClick={() => setActiveFilter(category.value)}
                    >
                        {category.label}
                    </button>
                ))}
            </div>

            {loading && (
                <div className="loading-container">
                    <p>Loading certificates...</p>
                </div>
            )}

            {error && (
                <div className="error-notice">
                    <p>{error}</p>
                </div>
            )}

            <div className="certificates-grid">
                {filteredCertificates.map(certificate => (
                    <div className="certificate-card" key={certificate.certificate_id}>
                        <div className="certificate-image">
                            <img
                                src={certificate.certificate_image}
                                alt={certificate.title}
                                onError={handleImageError}
                            />
                        </div>
                        <div className="certificate-info">
                            <h3>{certificate.title}</h3>
                            <p className="issuer">{certificate.issuer}</p>
                            <p className="date">{certificate.date}</p>
                            <button
                                className="download-btn"
                                onClick={() => viewCertificate(certificate.certificate_image, certificate.title)}
                            >
                                View Certificate
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {!loading && filteredCertificates.length === 0 && (
                <div className="no-certificates">
                    <p>No certificates found in this category.</p>
                </div>
            )}
        </div>
    );
}

export default CertificateSection;